<script>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import ButtonListGrid from '@/assets/js/src/components/ButtonListGrid.vue'
import '@/assets/js/src/style/rtl.scss'
import { mapActions, mapState, } from 'pinia'
import { useResourceListStore, } from '@/assets/js/src/modules/resource/_pinia/resourceList'
import { useResourceStore, } from '@/assets/js/src/modules/resource/_pinia/resource'
import { mapUserOptionByPath, } from '@/assets/js/src/modules/user/_pinia/mapUserOptionByPath'
import { useDictionaryListFilter, } from '@/assets/js/src/modules/resource/_composables/useDictionaryListFilter'
import { useComponentIsActive, } from '@/assets/js/src/util/composables/useComponentIsActive'
import { useStripMarkdown, } from '@/assets/js/src/util/composables/useStripMarkdown'
import { computed, } from 'vue'
import H1Title from '@/assets/js/src/components/overlay/H1Title.vue'
import ResourceAbbrivationFilter from '@/assets/js/src/components/forms/ResourceAbbrivationFilter.vue'
import SearchFilter from '@/assets/js/src/components/forms/SearchFilter.vue'
import GridListContent from '@/assets/js/src/components/overlay/GridListContent.vue'

export default {
    name: 'DictionaryList',
    components: {
        GridListContent,
        SearchFilter,
        ResourceAbbrivationFilter,
        H1Title,
        ButtonListGrid,
        Overlay,
    },
    props: {
        asyncStatus: {
            type: Number,
            default: 1,
        },
    },
    setup () {
        return {
            ...useDictionaryListFilter(),
            ...useComponentIsActive(),
            ...useStripMarkdown(),
            gridView: computed(mapUserOptionByPath('resource/dictionary/gridView')),
        }
    },
    computed: {
        ...mapState(useResourceListStore, [
            'resourceItems',
            'page',
            'maxPages',
        ]),
        ...mapState(useResourceStore,[
            'resources',
        ]),
        dictionaryEnum: function () {
            return Object.values(this.resources)
                .reduce((acc, resource) => {
                    let abbreviations = acc.map(resource => resource.abbreviation)
                    if(resource.provider.split('_')[0] === 'dictionary' && !abbreviations.includes(resource.abbreviation)) {
                        acc.push(resource)
                    }

                    return acc
                },[])
                .map((resource) => ({
                    title: resource.name,
                    value: resource.abbreviation,
                }))
        },
        dictionaryItems () {
            return this.resourceItems.map((dictionaryItem)=>{
                let { content, id, resource, title, } = dictionaryItem

                return {
                    id,
                    title,
                    content: this.stripMarkdown(content),
                    resourceId: resource.id,
                }
            })
        },
    },
    methods: {
        ...mapActions(useResourceListStore,[
            'getResourceItems',
        ]),
        async getMoreResults () {
            if (this.page === this.maxPages) {
                return
            }
            let next = this.page + 1
            await this.getResourceItems({ page: next, provider: 'dictionary', })

            this.$bsa && this.$bsa.event({
                eventCategory: 'load more',
                eventAction: 'Wörterbücher nachladen',
                eventLabel: next,
            })
        },
    },
}
</script>

<template>
    <overlay
        class-name="dictionary-list"
        full-width
    >
        <v-row no-gutters>
            <v-col
                cols="12"
                md="6"
                offset-md="3"
            >
                <div class="d-flex">
                    <h1-title :title-key="'async.resource.dictionary.list.title'" />

                    <v-spacer></v-spacer>

                    <button-list-grid
                        :grid-view="gridView"
                        @toggle="gridView = !gridView"
                    />
                </div>

                <resource-abbrivation-filter
                    v-model="abbreviations"
                    :items="dictionaryEnum"
                    label-key="async.resource.dictionary.list.filter"
                />

                <search-filter
                    v-model="debouncedSearch"
                    label-key="async.resource.dictionary.list.filter_placeholder"
                />
            </v-col>
        </v-row>

        <grid-list-content
            :items="dictionaryItems"
            :async-status="asyncStatus"
            :page="page"
            :max-pages="maxPages"
            :grid-view="gridView"
            :loading-props="gridView
                ? {height:'122px',width:'calc(100% - 8px)',margin:'8px'}
                : {height:'82px',width:'100%',margin:'16px 0'}"
            no-items-key="async.resource.dictionary.list.no_filtered_items"
            @load-more="getMoreResults"
        >
            <template #item="{item: dictionaryItem, index,}">
                <router-link
                    :class="[{'first': index === 0,'elevation-1':gridView}, `dictionary-list--${gridView?'grid':'list'}__item`,]"
                    :dir="resources[dictionaryItem.resourceId].language.rtl ? DIR_RTL : DIR_LTR"
                    :to="{path:`/dictionary/${encodeURIComponent(resources[dictionaryItem.resourceId].abbreviation)}/${encodeURIComponent(dictionaryItem.title)}/${dictionaryItem.id}`}"
                >
                    <div
                        :class="`dictionary-list--${gridView?'grid':'list'}__item--limiter`"
                    >
                        <h2 class="text-truncate">
                            {{ dictionaryItem.title }}
                        </h2>
                        <span
                            v-show="gridView"
                            class="text-truncate dictionary-list--grid__item__subtitle"
                        >{{ resources[dictionaryItem.resourceId].name }}</span>

                        <p :class="`dictionary-list--${gridView?'grid':'list'}__item__content`">
                            {{ dictionaryItem.content }}
                        </p>
                    </div>
                    <p
                        v-show="!gridView"
                        class="dictionary-list--list__item__meta"
                    >
                        {{ resources[dictionaryItem.resourceId].name }}
                    </p>
                </router-link>
            </template>
        </grid-list-content>
    </overlay>
</template>

<style lang="scss">
.bs-app {
    .dictionary-list {
        &--list {
            &__item {
                display: flex;
                padding: 20px 0 16px;
                text-decoration: none;
                cursor: pointer;

                &:not(.first) {
                    border-top: 1px solid var(--bs-color-grey);
                }

                h2 {
                    margin-bottom: 4px;
                    color: map-deep-get($bs-color, bsHighlight);
                    font-weight: 500;
                    font-size: 18px;
                }

                &:hover, &:focus {
                    background-color: var(--bs-color-white);

                    h2 {
                        font-weight: 700;
                    }
                }

                &--limiter {
                    flex-basis: 75%;
                    flex-shrink: 0;
                    max-width: 75%;
                    padding: 0 16px;
                }

                &__content {
                    display: -webkit-box;
                    max-height: calc(4 * 21px);
                    margin: 0;
                    overflow: hidden;
                    color: var(--bs-color-black);
                    font-size: 14px;
                    hyphens: auto;
                    -webkit-line-clamp: 4;
                    -webkit-box-orient: vertical;
                }

                &__meta {
                    display: flex;
                    flex-basis: 25%;
                    flex-direction: column;
                    justify-content: space-between;
                    margin: 0;
                    padding-right: 16px;
                    color: var(--bs-color-overlay-grey-dark);
                    font-size: 14px;
                    hyphens: auto;
                }
            }

            &__loading-dummy {
                border-top: 1px solid map-deep-get($bs-color, grey);

                @include dark {
                    border-top: 1px solid map-deep-get($bs-color, invert, greyDark);
                }
            }
        }

        &--grid {
            &__item {
                display: flex;
                height: 122px;
                margin: map-deep-get($bs-xl, overlay, grid, margin);
                padding: 20px 10px;
                overflow: hidden;
                text-decoration: none;
                background-color: var(--bs-color-white);

                h2 {
                    margin-bottom: 4px;
                    color: map-deep-get($bs-color, bsHighlight);
                    font-weight: 500;
                    font-size: 18px;
                }

                &:hover, &:focus {
                    h2 {
                        font-weight: 700;
                    }
                }

                &--limiter {
                    height: 90px;
                    overflow: hidden;

                    @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                        height: 90px;
                    }
                }

                &__subtitle {
                    display: block;
                    color: var(--bs-color-black);
                    font-size: 14px;
                    line-height: 1.6em !important;
                }

                &__content {
                    display: -webkit-box;
                    margin: 0;
                    overflow: hidden;
                    color: map-deep-get($bs-color, autocomplete, font);
                    font-size: 14px;
                    hyphens: auto;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
            }
        }
    }
}
</style>
